import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../config/api';
import { formatMatchDate } from '../../utils/matchDateTime';
import { FaArrowLeft, FaEnvelope, FaBan, FaUsers, FaSync } from 'react-icons/fa';
import './AdminPages.css';

const TICKET_FROM_EMAIL = 'ticket@oltrefrontiera.ch';

const AdminTicketSubscriberNotify = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [responses, setResponses] = useState(null);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const notif = ticket?.subscriberNotification || {};
  const alreadyDecided = notif.sent || notif.skipped;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [ticketRes, subsRes] = await Promise.all([
          api.get(`/tickets/${id}`),
          api.get('/tickets/subscribers-for-season'),
        ]);
        setTicket(ticketRes.data);
        const subs = subsRes.data.subscribers || [];
        setSubscribers(subs);
        setSelectedIds(new Set(subs.map((s) => String(s._id))));

        if (ticketRes.data.subscriberNotification?.sent) {
          const respRes = await api.get(`/ticket-subscriber-responses/ticket/${id}`);
          setResponses(respRes.data);
        }
      } catch (err) {
        console.error(err);
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const selectableIds = useMemo(
    () => subscribers.map((s) => String(s._id)),
    [subscribers],
  );

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((sid) => selectedIds.has(sid));
  const someSelected = selectableIds.some((sid) => selectedIds.has(sid));
  const selectedCount = selectableIds.filter((sid) => selectedIds.has(sid)).length;

  const missingCount = useMemo(() => {
    if (!notif.sent || !subscribers.length) return 0;
    const respondedIds = new Set(
      (responses?.responses || []).map((r) => String(r.user?._id || r.user)),
    );
    return subscribers.filter((s) => !respondedIds.has(String(s._id))).length;
  }, [notif.sent, subscribers, responses]);

  const toggleOne = (userId) => {
    const idStr = String(userId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(idStr)) next.delete(idStr);
      else next.add(idStr);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds));
    }
  };

  const handleNotify = async (send) => {
    const selectedList = selectableIds.filter((sid) => selectedIds.has(sid));

    const msg = send
      ? `Confirmer l'envoi des e-mails à ${selectedList.length} abonné(s) sélectionné(s) depuis ${TICKET_FROM_EMAIL} ?`
      : 'Créer le billet sans envoyer d\'e-mails aux abonnés ?';
    if (!window.confirm(msg)) return;

    try {
      setSubmitting(true);
      await api.post(`/tickets/${id}/notify-subscribers`, {
        send,
        subscriberIds: send ? selectedList : undefined,
      });
      if (send) {
        const respRes = await api.get(`/ticket-subscriber-responses/ticket/${id}`);
        setResponses(respRes.data);
      }
      const ticketRes = await api.get(`/tickets/${id}`);
      setTicket(ticketRes.data);
      alert(send ? 'E-mails envoyés avec succès' : 'Billet enregistré sans envoi d\'e-mails');
      if (!send) {
        navigate('/admin/tickets');
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.response?.data?.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncMissing = async () => {
    const msg = missingCount > 0
      ? `Synchroniser ${missingCount} abonné(s) manquant(s) ? Un e-mail leur sera envoyé depuis ${TICKET_FROM_EMAIL}.`
      : 'Vérifier s\'il reste des abonnés à synchroniser pour ce billet ?';
    if (!window.confirm(msg)) return;

    try {
      setSyncing(true);
      const { data } = await api.post(`/tickets/${id}/sync-missing-subscribers`);
      const { results } = data;
      const respRes = await api.get(`/ticket-subscriber-responses/ticket/${id}`);
      setResponses(respRes.data);

      const parts = [];
      if (results.added) parts.push(`${results.added} ajouté(s)`);
      if (results.sent) parts.push(`${results.sent} e-mail(s) envoyé(s)`);
      if (results.skipped) parts.push(`${results.skipped} ignoré(s)`);
      if (results.errors?.length) parts.push(`${results.errors.length} erreur(s)`);
      alert(parts.length ? parts.join(', ') : 'Aucun abonné manquant à synchroniser');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.response?.data?.message || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const formatAdminDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSeat = (sub) => {
    const s = sub.subscription;
    if (!s) return '—';
    const parts = [s.tribune, s.settore, s.fila, s.posto].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortIndicator = (field) => (sortBy === field ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : '');

  const subscriberAboById = useMemo(() => {
    const map = new Map();
    for (const sub of subscribers) {
      map.set(String(sub._id), sub.aboTypes || []);
    }
    return map;
  }, [subscribers]);

  const getAboTypesForRow = (row) => {
    if (!responses) return row.aboTypes || [];
    const userId = String(row.user?._id || row.user);
    return subscriberAboById.get(userId) || [];
  };

  const tableRows = useMemo(() => {
    const rows = responses ? responses.responses : subscribers;
    const sorted = [...rows];

    const aboTypesForRow = (row) => {
      if (!responses) return row.aboTypes || [];
      const userId = String(row.user?._id || row.user);
      return subscriberAboById.get(userId) || [];
    };

    const getAboLabel = (row) => aboTypesForRow(row).join(', ').toLowerCase();

    sorted.sort((a, b) => {
      const subA = responses ? a.user : a;
      const subB = responses ? b.user : b;
      let compareA;
      let compareB;

      if (sortBy === 'aboType') {
        compareA = getAboLabel(a);
        compareB = getAboLabel(b);
      } else {
        compareA = `${subA?.lastName || ''} ${subA?.firstName || ''}`.trim().toLowerCase();
        compareB = `${subB?.lastName || ''} ${subB?.firstName || ''}`.trim().toLowerCase();
      }

      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [responses, subscribers, sortBy, sortOrder, subscriberAboById]);

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <p style={{ color: '#c0392b' }}>{error || 'Billet introuvable'}</p>
          <button type="button" className="btn-back" onClick={() => navigate('/admin/tickets')}>
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <button
            type="button"
            className="btn-back"
            onClick={() => navigate('/admin/tickets')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--juve-black)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              marginBottom: '1rem',
            }}
          >
            <FaArrowLeft /> Retour aux billets
          </button>
          <h1>
            <FaUsers style={{ marginRight: '0.5rem' }} />
            Notification des abonnés
          </h1>
        </div>

        <div
          style={{
            padding: '1rem 1.25rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #e0e0e0',
          }}
        >
          <p style={{ margin: '0 0 4px', fontWeight: 700 }}>{ticket.matchTitle}</p>
          <p style={{ margin: 0, color: '#555' }}>
            {formatMatchDate(ticket.date, 'fr-FR')} — {ticket.stadium}
          </p>
          {ticket.subscriberResponseDeadline && (
            <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#2980b9' }}>
              Délai de réponse des abonnés : <strong>{formatMatchDate(ticket.subscriberResponseDeadline, 'fr-FR')}</strong>
            </p>
          )}
        </div>

        {alreadyDecided && (
          <div
            style={{
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              borderRadius: '6px',
              background: notif.sent ? '#d4edda' : '#fff3cd',
              border: `1px solid ${notif.sent ? '#c3e6cb' : '#ffeeba'}`,
            }}
          >
            {notif.sent
              ? `E-mails envoyés le ${formatAdminDate(notif.sentAt)}`
              : `Envoi d'e-mails ignoré le ${formatAdminDate(notif.skippedAt)}`}
          </div>
        )}

        {responses && (
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              marginBottom: '1.5rem',
            }}
          >
            {[
              { label: 'Total', value: responses.stats.total },
              { label: 'Réponses', value: responses.stats.responded },
              { label: 'En attente', value: responses.stats.pending },
              { label: 'Participent', value: responses.stats.participate },
              { label: 'Abo dispo', value: responses.stats.available },
              { label: 'Gardent abo', value: responses.stats.keep },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  padding: '0.75rem 1rem',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{s.value}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <p style={{ marginBottom: '1rem', color: '#555' }}>
          {subscribers.length} abonné(s) BASE, FULL, STAR ou STAR abo club pour la saison en cours.
          {!alreadyDecided && (
            <>
              {' '}
              Cochez les destinataires puis confirmez l&apos;envoi depuis{' '}
              <strong>{TICKET_FROM_EMAIL}</strong>.
            </>
          )}
        </p>

        {!alreadyDecided && subscribers.length > 0 && (
          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#2980b9' }}>
            {selectedCount} abonné(s) sélectionné(s) sur {subscribers.length}
          </p>
        )}

        <div className="admin-table-container" style={{ marginBottom: '1.5rem' }}>
          <table className="admin-table">
            <thead>
              <tr>
                {!alreadyDecided && !responses && (
                  <th style={{ width: '44px' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={toggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                )}
                <th className="sortable" onClick={() => handleSort('name')}>
                  Nom{sortIndicator('name')}
                </th>
                <th>E-mail</th>
                <th className="sortable" onClick={() => handleSort('aboType')}>
                  Type abo{sortIndicator('aboType')}
                </th>
                <th>Place</th>
                {responses && <th>Réponse</th>}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => {
                const sub = responses ? row.user : row;
                const resp = responses ? row : null;
                const rowId = String(sub._id || row._id);
                return (
                  <tr key={rowId}>
                    {!alreadyDecided && !responses && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(rowId)}
                          onChange={() => toggleOne(rowId)}
                          aria-label={`Sélectionner ${sub.firstName} ${sub.lastName}`}
                        />
                      </td>
                    )}
                    <td>
                      {sub.firstName} {sub.lastName}
                    </td>
                    <td>{sub.email}</td>
                    <td>{getAboTypesForRow(row).join(', ') || '—'}</td>
                    <td>{formatSeat(sub)}</td>
                    {responses && (
                      <td>
                        {resp.choiceLabel ? (
                          <span
                            style={{
                              fontSize: '0.85rem',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background:
                                resp.choice === 'participate'
                                  ? '#d4edda'
                                  : resp.choice === 'available'
                                    ? '#cce5ff'
                                    : resp.choice === 'keep'
                                      ? '#f8d7da'
                                      : '#f0f0f0',
                            }}
                          >
                            {resp.choiceLabel}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>En attente</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {tableRows.length === 0 && (
                <tr>
                  <td
                    colSpan={responses ? 5 : alreadyDecided ? 4 : 5}
                    style={{ textAlign: 'center', color: '#999' }}
                  >
                    Aucun abonné trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!alreadyDecided && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              className="btn-add"
              disabled={submitting || selectedCount === 0}
              onClick={() => handleNotify(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FaEnvelope />
              {submitting
                ? 'Envoi en cours…'
                : `Confirmer l'envoi (${selectedCount})`}
            </button>
            <button
              type="button"
              className="btn-edit"
              disabled={submitting}
              onClick={() => handleNotify(false)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FaBan />
              Ne pas envoyer d'e-mail
            </button>
            <span style={{ fontSize: '0.85rem', color: '#666' }}>
              Expéditeur : {TICKET_FROM_EMAIL}
            </span>
          </div>
        )}

        {alreadyDecided && notif.sent && (
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: '1rem',
            }}
          >
            <button
              type="button"
              className="btn-add"
              disabled={syncing}
              onClick={handleSyncMissing}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FaSync />
              {syncing
                ? 'Synchronisation…'
                : `Synchroniser les abonnés manquants${missingCount > 0 ? ` (${missingCount})` : ''}`}
            </button>
            {missingCount > 0 && (
              <span style={{ fontSize: '0.85rem', color: '#666' }}>
                Abonnés de la saison courante sans ligne de réponse pour ce billet
              </span>
            )}
            <button
              type="button"
              className="btn-edit"
              onClick={() => navigate('/admin/tickets')}
            >
              Retour aux billets
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTicketSubscriberNotify;
